import { Fragment, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Container, Spinner } from "reactstrap";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { useRanks } from "../../Hooks/use-ranks";
import { DiagonalCard } from "../../Components/common/DiagonalCard";
import { StatusChip } from "../../Components/common/StatusChip";
import { CardUserInfo } from "../../Components/user/CardUserInfo";
import { CardUserWallet } from "../../Components/user/CardUserWallet";
import { CardUserNetwork } from "../../Components/user/CardUserNetwork";
import { CardUserBankAccount } from "../../Components/user/CardUserBankAccount";
import { EditUserModal } from "../../Components/user/EditUserModal";
import { UserBonusModal } from "../../Components/user/UserBonusModal";
import InfluencerTab from "../../Components/user/InfluencerTab";
import LoyaltyTab from "../../Components/user/LoyaltyModern";
import { getRankTone } from "../../utils/rankTone";
import { getStatusMeta } from "../../utils/statusMeta";
import { ORDER_STATUS_LABELS } from "../../utils/orderConstants";
import { formatDate } from "../../utils/formatDate";

const TABS = ["Resumen", "Bono influencer", "Regalías", "Órdenes", "Red"];

const OrdersTable = ({ orders, loading }) => (
  <div style={{ overflowX: "auto" }}>
    <table className="ui-table" style={{ minWidth: 820 }}>
      <thead>
        <tr className="ui-table__head">
          <th>ID</th>
          <th>Fecha</th>
          <th style={{ textAlign: "center" }}>Cant.</th>
          <th style={{ textAlign: "right" }}>Total</th>
          <th>Estado</th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr><td colSpan={5} className="ui-table__empty"><i className="ri-loader-4-line" /><span>Cargando...</span></td></tr>
        ) : orders.length === 0 ? (
          <tr><td colSpan={5} className="ui-table__empty"><i className="ri-inbox-line" /><span>Sin órdenes registradas.</span></td></tr>
        ) : (
          orders.map((o) => {
            const meta = getStatusMeta(o.status);
            const qty = o.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0;
            return (
              <tr key={o.id}>
                <td style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11.5, color: "#5A6187" }}>
                  {o.id.slice(0, 10)}…
                </td>
                <td style={{ color: "#5A6187" }}>{formatDate(o.created_at)}</td>
                <td style={{ textAlign: "center" }}>{qty}</td>
                <td style={{ textAlign: "right", fontWeight: 700 }}>${(o.total || 0).toLocaleString()}</td>
                <td>
                  <StatusChip tone={meta.tone} icon={meta.icon} sm>
                    {ORDER_STATUS_LABELS[o.status] || o.status}
                  </StatusChip>
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  </div>
);

export const DetailUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getData } = useDataFetch();
  const [tab, setTab] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [bonusOpen, setBonusOpen] = useState(false);

  const { data, isLoading } = getData(["user", id], `/users/byId/${id}`, {
    enabled: !!id,
  });

  const user = data?.data?.user;

  // currentPointsRank, no currentRank: éste último sale del histórico de
  // userranks (rango más alto alcanzado alguna vez); el primero se deriva
  // de los puntos vigentes — mismo criterio que usa LoyaltyModern.jsx.
  const { currentPointsRank, currentUserPoints } = useRanks(user?.id);

  const { data: ordersData, isLoading: loadingOrders } = getData(
    ["order", { user: id }, "detail-user"],
    "/order",
    { enabled: !!id },
    { user: id, page: 1, limit: 5 }
  );
  const orders = ordersData?.data?.orders ?? [];
  const totalOrders = ordersData?.data?.total ?? orders.length;

  // Pestaña "Órdenes": misma llamada a /order, sin el limit:5 del resumen y
  // paginada en cliente. Sólo se dispara cuando la pestaña está activa.
  const [ordersTabPage, setOrdersTabPage] = useState({ pageIndex: 0, pageSize: 10 });
  const { data: allOrdersData, isLoading: loadingAllOrders } = getData(
    ["order", { user: id, page: ordersTabPage.pageIndex + 1, limit: ordersTabPage.pageSize }, "detail-user-all"],
    "/order",
    { enabled: !!id && tab === 3 },
    { user: id, page: ordersTabPage.pageIndex + 1, limit: ordersTabPage.pageSize }
  );
  const allOrders = allOrdersData?.data?.orders ?? [];
  const totalAllOrders = allOrdersData?.data?.total ?? allOrders.length;

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" /> Cargando usuario...
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-5">
        <h5>No se encontró el usuario</h5>
        <button type="button" className="ui-back" onClick={() => navigate(-1)}>
          <i className="ri-arrow-left-line" /> Volver
        </button>
      </div>
    );
  }

  const rankName = currentPointsRank?.name;
  const rankTone = getRankTone(rankName);

  return (
    <Fragment>
    <Container fluid={true} className="ui-content" style={{ padding: 0 }}>
      <button type="button" className="ui-back" onClick={() => navigate(-1)}>
        <i className="ri-arrow-left-line" /> Volver a usuarios
      </button>

      <div style={{ marginBottom: 16 }}>
        <DiagonalCard>
          <div style={{ display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap" }}>
            <div style={{ width: 70, height: 70, borderRadius: "50%", background: "#EDEFF6", display: "flex", alignItems: "center", justifyContent: "center", color: "#9AA1BC", flexShrink: 0 }}>
              <i className="ri-user-line" style={{ fontSize: 30 }} />
            </div>
            <div style={{ flex: 1, minWidth: 230 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 11, flexWrap: "wrap" }}>
                <h2 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: "-0.03em" }}>{user.name}</h2>
                <StatusChip tone={user.is_confirmed ? "success" : "danger"} icon={user.is_confirmed ? "ri-checkbox-circle-line" : "ri-close-circle-line"}>
                  {user.is_confirmed ? "Autenticado" : "No autenticado"}
                </StatusChip>
                {rankName && (
                  <span className="ui-chip" style={{ color: rankTone.color, background: rankTone.bg, borderColor: `${rankTone.color}33` }}>
                    <i className="ri-medal-line" /> {rankName}
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 18, marginTop: 7, color: "#5A6187", fontSize: 12.5, flexWrap: "wrap" }}>
                <span style={{ fontFamily: "'JetBrains Mono',monospace" }}>{user.multyhealth_code}</span>
                <span>{user.email}</span>
                <span>{user.document}</span>
                <span>{user.country_id?.name}</span>
              </div>
              <div style={{ color: "#8A91AE", fontSize: 12, marginTop: 5 }}>
                Auspiciador: {user.reference_id ? `${user.reference_id.multyhealth_code} · ${user.reference_id.name}` : "Sin auspiciador"}
              </div>
            </div>
            <div style={{ display: "flex", gap: 9 }}>
              <button type="button" className="ui-back" style={{ margin: 0, border: "1px solid #E1E4F0", borderRadius: 9, padding: "10px 14px" }} onClick={() => navigate(`/user/referral-network/${user.id}`)}>
                <i className="ri-node-tree" /> Ver red
              </button>
              <button type="button" className="ui-back" style={{ margin: 0, border: "1px solid #E1E4F0", borderRadius: 9, padding: "10px 14px" }} onClick={() => setEditOpen(true)}>
                <i className="ri-edit-line" /> Editar
              </button>
              <button
                type="button"
                style={{ display: "flex", alignItems: "center", gap: 7, border: "none", background: "#4B36F0", color: "#fff", borderRadius: 9, padding: "10px 15px", fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                onClick={() => setBonusOpen(true)}
              >
                <i className="ri-gift-line" /> Bonificaciones
              </button>
            </div>
          </div>
        </DiagonalCard>
      </div>

      <div className="ui-tabs">
        {TABS.map((label, i) => (
          <button
            key={label}
            type="button"
            className={`ui-tabs__item${i === tab ? " ui-tabs__item--active" : ""}`}
            onClick={() => setTab(i)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 0 && (
        <>
          <div className="ui-grid-thirds" style={{ marginBottom: 16 }}>
            <CardUserWallet wallet={user.network?.wallet} />
            <CardUserBankAccount user={user} />
            <CardUserNetwork user={user} monthlyPoints={currentUserPoints} />
          </div>
          <div className="ui-grid-thirds" style={{ marginBottom: 16 }}>
            <CardUserInfo user={user} />
          </div>
          <div className="ui-card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Órdenes del usuario</h3>
              <Link to="/order" style={{ color: "#4B36F0", fontSize: 12.5, fontWeight: 600 }}>
                Ver todas ({totalOrders})
              </Link>
            </div>
            <OrdersTable orders={orders} loading={loadingOrders} />
          </div>
        </>
      )}

      {tab === 1 && <InfluencerTab user={user} />}
      {tab === 2 && <LoyaltyTab user={user} />}

      {tab === 3 && (
        <div className="ui-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Órdenes del usuario</h3>
          </div>
          <OrdersTable orders={allOrders} loading={loadingAllOrders} />
          <div className="ui-table-card__footer" style={{ padding: "14px 0 0", border: 0 }}>
            <span>
              {totalAllOrders > 0
                ? `Mostrando ${ordersTabPage.pageIndex * ordersTabPage.pageSize + 1}-${Math.min((ordersTabPage.pageIndex + 1) * ordersTabPage.pageSize, totalAllOrders)} de ${totalAllOrders}`
                : "Sin órdenes registradas."}
            </span>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="ui-page-btn"
                disabled={ordersTabPage.pageIndex === 0}
                onClick={() => setOrdersTabPage((p) => ({ ...p, pageIndex: p.pageIndex - 1 }))}
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              <span className="ui-page-btn ui-page-btn--active">{ordersTabPage.pageIndex + 1}</span>
              <button
                type="button"
                className="ui-page-btn"
                disabled={(ordersTabPage.pageIndex + 1) * ordersTabPage.pageSize >= totalAllOrders}
                onClick={() => setOrdersTabPage((p) => ({ ...p, pageIndex: p.pageIndex + 1 }))}
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === 4 && (
        <div className="ui-grid-thirds">
          <CardUserNetwork user={user} monthlyPoints={currentUserPoints} />
        </div>
      )}
    </Container>

      <EditUserModal isOpen={editOpen} toggle={() => setEditOpen(false)} user={user} />
      <UserBonusModal isOpen={bonusOpen} toggle={() => setBonusOpen(false)} user={user} />
    </Fragment>
  );
};
